import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDebugMode } from "../contexts/DebugModeContext";
import { Button } from "../components/ui/button";
import { Loader2, Save, Trash2, ArrowLeft } from "lucide-react";

import { useWorkout, WorkoutProvider } from "@/contexts/WorkoutContext";
import { useWorkoutOperations } from "@/hooks/useWorkoutOperations";
import { useWorkoutEditorActions } from "../hooks/useWorkoutEditorActions";
import { WorkoutHeader } from "@/components/WorkoutHeader";
import { WorkoutDetails } from "@/components/WorkoutDetails";
import { EmptyExerciseState } from "@/components/EmptyExerciseState";
import { WorkoutTable } from "../components/workout/WorkoutTable";
import { WorkoutActionsBar } from "../components/workout/WorkoutActionsBar";
import { DebugOverlay } from "../components/workout/DebugOverlay";

import { z } from "zod";
import { ExerciseSchema, WorkoutSchema } from "../schemas/workout";

export type Exercise = z.infer<typeof ExerciseSchema>;
export type Workout = z.infer<typeof WorkoutSchema>;

// Main component wrapper with provider
export default function EditWorkoutPageWrapper() {
  return (
    <WorkoutProvider>
      <EditWorkoutPage />
    </WorkoutProvider>
  );
}

// Main component implementation
function EditWorkoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    isLoading,
    isSaving,
    isDirty,
    workout,
    editingField,
    error,
    dispatch,
  } = useWorkout();

  const {
    loadWorkout,
    saveWorkout,
    deleteWorkout,
  } = useWorkoutOperations();

  const {
    handleWorkoutNameChange,
    handleDescriptionChange,
    handleAddExercise,
    handleExerciseChange,
    handleExerciseDelete,
    handleExerciseDuplicate,
    handleExerciseTypeChange,
    handleExerciseDetailsChange,
    startEditing,
    stopEditing,
  } = useWorkoutEditorActions();

  const { debugMode, toggleDebugMode } = useDebugMode();

  // Load workout data on mount
  useEffect(() => {
    if (id) {
      loadWorkout(id);
    }
  }, [id]);

  // Set up keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        toggleDebugMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [toggleDebugMode]);

  const handleReorderExercises = (newExercises: Exercise[]) => {
    if (!workout) return;
    dispatch({ type: "REORDER_EXERCISES", payload: newExercises });
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading workout...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || !workout) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center">
        <div className="bg-red-100 dark:bg-red-900/20 p-6 rounded-full mb-4">
          <Trash2 className="h-10 w-10 text-red-500 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Workout not found</h2>
        <p className="text-muted-foreground mb-4 max-w-md">
          The workout you&apos;re looking for doesn&apos;t exist or you
          don&apos;t have permission to view it.
        </p>
        <Button onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Workouts
        </Button>
      </div>
    );
  }

  // Render main content
  return (
    <>
      <div className="container max-w-4xl mx-auto py-6 px-4 sm:px-6">
        <WorkoutHeader
          workout={workout}
          isDirty={isDirty}
          isSaving={isSaving}
          navigate={navigate}
          handleSave={saveWorkout}
          handleDelete={deleteWorkout}
        />

        <WorkoutDetails
          workout={workout}
          handleNameChange={handleWorkoutNameChange}
          handleDescriptionChange={handleDescriptionChange}
        />

        {/* Exercises section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Exercises</h2>
          </div>

          {workout.exercises.length === 0 ? (
            <EmptyExerciseState handleAddExercise={handleAddExercise} />
          ) : (
            <>
              <WorkoutTable
                exercises={workout.exercises}
                editingField={editingField}
                debugMode={debugMode}
                startEditing={startEditing}
                stopEditing={stopEditing}
                handleExerciseChange={handleExerciseChange}
                handleExerciseDuplicate={handleExerciseDuplicate}
                handleExerciseDelete={handleExerciseDelete}
                handleExerciseTypeChange={handleExerciseTypeChange}
                handleExerciseDetailsChange={handleExerciseDetailsChange}
                onReorderExercises={handleReorderExercises}
              />
              <WorkoutActionsBar
                exerciseCount={workout.exercises.length}
                onAddExercise={handleAddExercise}
              />
            </>
          )}
        </div>

        {/* Floating save button for mobile */}
        <div className="fixed bottom-6 right-6 sm:hidden">
          <Button
            onClick={saveWorkout}
            disabled={isSaving || !isDirty}
            size="lg"
            className="rounded-full shadow-lg h-14 w-14 p-0"
          >
            {isSaving ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Save className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      <DebugOverlay
        debugMode={debugMode}
        workoutId={id}
        isDirty={isDirty}
        exerciseCount={workout?.exercises.length || 0}
      />
    </>
  );
}
